import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import QuranTree from "../components/QuranTree";
import { SocialShareModal } from "../components/SocialShare";
import { buildTreeCard } from "../lib/shareCard";
import { TreePine, Leaf, GitBranch, Clock, Flame, Users, Plus, Share2 } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const treeMessage = (ayahs, pages, t) => {
  if (ayahs === 0) return t("legacy.treeSeed");
  if (pages < 1) return t("legacy.treeLeavesSoFar", { count: ayahs });
  if (pages < 5) return t("legacy.treeTakingRoot", { count: Math.floor(pages), ayahs });
  return t("legacy.treeLegacy", { branches: Math.floor(pages), ayahs });
};

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl bg-sand-200/60 px-4 py-3 text-center" data-testid={testid}>
      <Icon className="h-4 w-4 mx-auto text-gold-600" />
      <div className="mt-1 font-display font-semibold text-xl text-forest-700">{value}</div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}

export default function Legacy() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [refCode, setRefCode] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  useEffect(() => {
    api.get("/legacy").then((r) => setData(r.data)).catch(() => {});
    api.get("/referrals").then((r) => setRefCode(r.data.ref_code)).catch(() => {});
  }, []);

  if (!data) return <div className="max-w-3xl mx-auto hifz-card h-96 animate-pulse-soft bg-sand-200/50" />;
  const { me, families } = data;
  const shareUrl = `${window.location.origin}/${refCode ? `?ref=${refCode}` : ""}`;
  const shareMessage = `My Quran Tree is growing — ${me.ayahs} ${me.ayahs === 1 ? "leaf" : "leaves"} (ayahs) and ${Math.floor(me.pages)} ${Math.floor(me.pages) === 1 ? "branch" : "branches"} (pages) so far, Alhamdulillah! 🌳 Grow yours with HikmahVerses:`;

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="legacy-page">
      <header>
        <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">{t("legacy.quranLegacy")}</div>
        <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">{t("legacy.yourQuranTree")}</h1>
        <p className="mt-2 text-foreground/70">{t("legacy.everyAyahAddsALeaf")}</p>
      </header>

      <section className="hifz-card" data-testid="tree-card">
        <QuranTree ayahs={me.ayahs} pages={me.pages} />
        <p className="mt-2 text-center text-sm text-foreground/75" data-testid="tree-message">
          {treeMessage(me.ayahs, me.pages, t)}
        </p>
        {/* C1 — streak dropped here; it's already shown near the bell icon
            (StreakBadge), so keeping it here too was a duplicate. */}
        <div className="mt-5 grid grid-cols-3 gap-3">
          <Stat icon={Leaf} label={t("legacy.leavesAyahs")} value={me.ayahs} testid="stat-leaves" />
          <Stat icon={GitBranch} label={t("legacy.branchesPages")} value={Math.floor(me.pages)} testid="stat-branches" />
          <Stat icon={Clock} label={t("legacy.hoursWithTheQuran")} value={me.hours} testid="stat-hours" />
        </div>
        <button onClick={() => setShareOpen(true)} className="btn-accent text-xs mt-5 mx-auto flex" data-testid="share-tree-btn">
          <Share2 className="h-3.5 w-3.5" /> {t("legacy.shareMyQuranTree")}
        </button>
      </section>

      <section data-testid="family-legacy">
        <h2 className="font-display font-semibold text-2xl text-forest-700 mb-4 inline-flex items-center gap-2">
          <TreePine className="h-5 w-5 text-gold-600" /> {t("legacy.familyLegacy")}
        </h2>
        {families.length === 0 ? (
          <div className="hifz-card border-dashed text-center p-8" data-testid="family-empty">
            <Users className="h-7 w-7 mx-auto text-gold-600" />
            <p className="mt-2 text-sm text-foreground/70">
              {t("legacy.aFamilyThatMemorizesTogether")}
            </p>
            <Link to="/app/community" className="btn-primary text-xs mt-4 inline-flex" data-testid="family-create-link">
              <Plus className="h-3.5 w-3.5" /> {t("legacy.createAFamilyCircle")}
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {families.map((f) => {
              const maxAyahs = Math.max(...f.members.map((m) => m.ayahs), 1);
              return (
                <div key={f.circle_id} className="hifz-card" data-testid={`family-${f.circle_id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h3 className="font-display font-semibold text-lg text-forest-700">{f.name}</h3>
                    <span className="text-xs text-muted-foreground">
                      {t("legacy.studiedToday", { active_today: f.combined.active_today, member_count: f.combined.member_count })}
                    </span>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <Stat icon={Leaf} label={t("legacy.ayahsTogether")} value={f.combined.ayahs} testid={`fam-ayahs-${f.circle_id}`} />
                    <Stat icon={GitBranch} label={t("legacy.pagesTogether")} value={Math.floor(f.combined.pages)} testid={`fam-pages-${f.circle_id}`} />
                    <Stat icon={Clock} label={t("legacy.hoursTogether")} value={f.combined.hours} testid={`fam-hours-${f.circle_id}`} />
                    <Stat icon={Flame} label={t("legacy.bestStreak")} value={f.combined.best_streak} testid={`fam-streak-${f.circle_id}`} />
                  </div>
                  <div className="mt-5 space-y-2.5">
                    {f.members.map((m) => (
                      <div key={m.name + m.ayahs} className="flex items-center gap-3" data-testid={`fam-member-${f.circle_id}`}>
                        <span className="w-28 shrink-0 truncate text-sm font-medium text-forest-700">
                          {m.name}{m.is_me ? " (you)" : ""}
                        </span>
                        <div className="flex-1 h-2.5 rounded-full bg-sand-200 overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-forest-700 to-gold-500"
                            style={{ width: `${Math.max(4, (m.ayahs / maxAyahs) * 100)}%` }} />
                        </div>
                        <span className="w-20 shrink-0 text-right text-xs text-muted-foreground">
                          {t("legacy.ayahsH", { ayahs: m.ayahs, hours: m.hours })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <SocialShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        buildCanvas={() => buildTreeCard({
          name: user?.name?.split(" ")[0] || "My",
          ayahs: me.ayahs, pages: me.pages, hours: me.hours, streak: me.streak,
          url: shareUrl,
        })}
        message={shareMessage}
        url={shareUrl}
        filename="my-quran-tree.png"
      />
    </div>
  );
}
